import { useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { DashboardLoadingOverlay } from './DashboardLoadingOverlay';
import { ChartWidget } from './widgets/ChartWidget';
import { HeaderWidget } from './widgets/HeaderWidget/HeaderWidget';
import {
  breakpointKeyList,
  getBreakpointFromWidth,
  getDashboardWidthWithGap,
  getWidgetRows,
  handleLayoutChange,
} from './layoutUtils';
import { AddWidgetButtons } from './AddWidgetButtons';
import {
  DASHBOARD_COL_NUMBER,
  DASHBOARD_GAP,
  DASHBOARD_MAX_WIDTH,
  DASHBOARD_MIN_WIDTH,
  DASHBOARD_NAME_EDIT_BUTTON_ID,
  DASHBOARD_NAME_ISLAND_ID,
  DRAGGABLE_CLASS_NAME,
  ROW_HEIGHT,
  WIDGET_ADD_BUTTON_ID,
} from './consts';
import ReactGridLayout, { Layout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import { useResizeDetector } from 'react-resize-detector';
import { GhostButton } from '@ardoq/button';
import { EditIcon } from '@ardoq/icons';
import { DashboardTrackingEventsNames } from './tracking';
import { colors, s16 } from '@ardoq/design-tokens';
import { PageHeaderIsland } from '@ardoq/page-layout';
import { NoteWidget } from './widgets/NoteWidget/NoteWidget';
import { PowerBiIframeWidget } from './widgets/PowerBiIframeWidget/PowerBiIframeWidget';
import {
  DashboardFilterBuilderIsland,
  DashboardFilterLinkModeIsland,
} from './filter/DashboardFilterIsland';
import {
  EditModeHeaderWidgetProps,
  EditModePowerBiIframeWidgetProps,
  EditModeNoteWidgetProps,
} from './widgets/renderTypes';
import {
  EditModeDashboardProps,
  EditModeWidgetProps,
} from './widgets/editModeTypes';
import { EditModeChartWidgetProps } from './widgets/widgetPropTypes';
import { type I18nLocale, t } from '@ardoq/i18n';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  /* stylelint-disable-next-line media-query-no-invalid */
  @media (min-width: ${DASHBOARD_MAX_WIDTH}px) {
    align-items: center;
  }
  height: 100%;
  min-width: ${getDashboardWidthWithGap(DASHBOARD_MIN_WIDTH)}px;
  max-width: ${DASHBOARD_MAX_WIDTH}px;
  margin-inline: auto;
  gap: ${s16};
`;

type WidgetsContainerProps = {
  $isResizing: boolean;
};

const WidgetsContainer = styled.div.attrs(() => ({
  role: 'grid',
  'aria-label': 'Dashboard widgets',
}))<WidgetsContainerProps>`
  width: 100%;
  max-width: ${DASHBOARD_MAX_WIDTH}px;

  .react-grid-item.resizing {
    /* sets z-index for grid item to appear above table header. Only works when you resize any widget, this class is absent otherwise
      without it any table header that interferes with resizable widget will display above it */
    z-index: 2;
  }

  .react-grid-item.react-grid-placeholder {
    /* This the target dropzone that appears when a widget is being dragged */
    background-color: ${colors.surfaceActive};
    opacity: 1;
    border: 2px dashed ${colors.borderActionActive};
    border-radius: 8px;
    z-index: auto;

    ${({ $isResizing }: WidgetsContainerProps) =>
      $isResizing &&
      css`
        opacity: 0.5;
        /* z-index of "resizable hint zone" placeholder. Should be on top of resizable widget itself */
        z-index: 3;
      `}
  }

  .react-grid-item > .react-resizable-handle {
    &::after {
      position: absolute;
      bottom: -2.5px;
      right: -2.5px;
      width: 9px;
      height: 9px;
      background-color: ${colors.iconAction};
      border: none;
      cursor: se-resize;
      opacity: 0;
    }

    &:hover {
      &::after {
        opacity: 1;
        background-color: ${colors.iconActionHover};
      }
    }

    &:active {
      &::after {
        opacity: 1;
        background-color: ${colors.iconActionActive};
      }
    }
  }
`;

const WidgetContainer = styled.div<WidgetsContainerProps>`
  outline: none;
  ${({ $isResizing }: WidgetsContainerProps) =>
    $isResizing &&
    css`
      outline: 1px solid ${colors.borderActionActive};
      /* sets z-index for widget wrapper to appear above table header and below grid placeholder */
      z-index: 3;
    `}
  border-radius: 8px;
  z-index: auto;

  &:hover {
    .react-resizable-handle {
      &::after {
        opacity: 1;
        background-color: ${colors.iconAction};
      }
    }
  }
`;

const isHeaderProps = (
  props: EditModeWidgetProps
): props is EditModeHeaderWidgetProps & { type: 'header' } =>
  props.type === 'header';

const isNoteProps = (
  props: EditModeWidgetProps
): props is EditModeNoteWidgetProps & { type: 'note' } => props.type === 'note';

const isPowerBiIframeProps = (
  props: EditModeWidgetProps
): props is EditModePowerBiIframeWidgetProps & { type: 'powerBiIframe' } =>
  props.type === 'powerBiIframe';

const isChartProps = (
  props: EditModeWidgetProps
): props is EditModeChartWidgetProps & { type: 'chart' } =>
  props.type === 'chart';

const EditModeDashboard = ({
  description,
  isLoading,
  pageBodyWidth,
  isSidebarRendered,
  selectedWidgetId,
  widgetsCommands,
  trackEvent,
  dashboardFilterIslandProps,
  isInLinkDashboardFiltersMode,
  widgetProps,
  trackOnDragStart,
  addWidgetButtonProps,
  getLayoutForCurrentBreakpoint,
  i18nLocale,
}: EditModeDashboardProps & {
  pageBodyWidth: number | null;
  isSidebarRendered: boolean;
  i18nLocale: I18nLocale;
}) => {
  const dashboardContainerRef = useRef<HTMLDivElement>(null);
  const { width, ref } = useResizeDetector({
    handleWidth: true,
    handleHeight: false,
    refreshMode: 'debounce',
    refreshRate: 50,
  });

  const dashboardWidth = width
    ? width > DASHBOARD_MAX_WIDTH
      ? DASHBOARD_MAX_WIDTH
      : width
    : DASHBOARD_MIN_WIDTH;

  const [isDragging, setIsDragging] = useState(false);
  const [resizingId, setResizingId] = useState<string | null>(null);

  if (isLoading) {
    return <DashboardLoadingOverlay i18nLocale={i18nLocale} />;
  }

  const currentBreakpoint = getBreakpointFromWidth(
    dashboardWidth,
    isSidebarRendered,
    pageBodyWidth
  );
  const layout = getLayoutForCurrentBreakpoint(currentBreakpoint, isDragging);

  const widgetBaseRows = getWidgetRows(
    breakpointKeyList.indexOf(currentBreakpoint)
  );

  const onResizeStop = (
    _layout: Layout[],
    oldItem: Layout,
    newItem: Layout
  ): void => {
    const isPowerBiIframeWidget =
      widgetProps.find(w => w._id === oldItem.i)?.type === 'powerBiIframe';

    if (isPowerBiIframeWidget) {
      const snapped = Math.round(newItem.h / widgetBaseRows) * widgetBaseRows;
      newItem.h = Math.max(widgetBaseRows, snapped);
    } else if (newItem.h === oldItem.h + 1) {
      newItem.h = oldItem.h;
    } else if (newItem.h > oldItem.h) {
      newItem.h = oldItem.h * 2;
    } else if (newItem.h === oldItem.h - 1) {
      newItem.h = oldItem.h;
    } else if (newItem.h < oldItem.h) {
      newItem.h = Math.round(oldItem.h / 2);
    }
    setResizingId(null);
  };

  return (
    <Container
      style={isDragging || resizingId ? { userSelect: 'none' } : {}}
      ref={dashboardContainerRef}
    >
      {dashboardFilterIslandProps.isInLinkDashboardFiltersMode ? (
        <DashboardFilterLinkModeIsland
          {...dashboardFilterIslandProps}
          i18nLocale={i18nLocale}
        />
      ) : (
        <DashboardFilterBuilderIsland
          {...dashboardFilterIslandProps}
          i18nLocale={i18nLocale}
        />
      )}
      <PageHeaderIsland
        isSelectable
        isSelected={selectedWidgetId === DASHBOARD_NAME_ISLAND_ID}
        description={description}
        descriptionPlaceholder={t(
          i18nLocale,
          'dashboard:dashboard.editingDescriptionPlaceholder',
          'Description'
        )}
        maxWidth={`${DASHBOARD_MAX_WIDTH}px`}
        buttons={
          !isInLinkDashboardFiltersMode && (
            <GhostButton
              dataTestId={DASHBOARD_NAME_EDIT_BUTTON_ID}
              data-tooltip-text={t(
                i18nLocale,
                'dashboard:dashboard.editTitleAndDescription',
                'Edit title and description'
              )}
              onClick={() =>
                widgetsCommands.selectWidget(DASHBOARD_NAME_ISLAND_ID)
              }
            >
              <EditIcon />
            </GhostButton>
          )
        }
        showButtonsOnlyOnHover
        trackEventExpandDescription={isExpanded =>
          trackEvent(
            DashboardTrackingEventsNames.CLICKED_EXPAND_DASHBOARD_DESCRIPTION_BUTTON,
            { clicked: !isExpanded ? 'Show less' : 'Show more' }
          )
        }
        showExpandDescriptionButton
      />
      <WidgetsContainer $isResizing={!!resizingId} ref={ref}>
        <ReactGridLayout
          margin={[DASHBOARD_GAP, DASHBOARD_GAP]}
          width={getDashboardWidthWithGap(dashboardWidth)}
          style={{
            zIndex: 0,
            margin: `0 -${DASHBOARD_GAP}px`,
          }} // React grid layout add outer margin, this is to reset that and only use margin between widgets. zIndex needs to be here to get the dropdown menu working above
          className="layout"
          layout={layout}
          draggableHandle={`.${DRAGGABLE_CLASS_NAME}`}
          onDragStart={trackOnDragStart}
          onDrag={() => {
            if (!isDragging) setIsDragging(true);
          }}
          onDragStop={() => {
            setIsDragging(false);
          }}
          rowHeight={ROW_HEIGHT}
          useCSSTransforms={false} // needed to be able to show dropdown menu
          cols={DASHBOARD_COL_NUMBER}
          onLayoutChange={newLayout =>
            handleLayoutChange(
              newLayout,
              currentBreakpoint,
              widgetsCommands.changeLayout
            )
          }
          resizeHandles={['se']}
          onResizeStart={(_layout, oldItem) => {
            setResizingId(oldItem.i);
          }}
          onResizeStop={onResizeStop}
        >
          {widgetProps.map(props => (
            <WidgetContainer
              $isResizing={resizingId === props._id}
              key={props._id}
            >
              {isHeaderProps(props) ? (
                <HeaderWidget isInEditMode {...props} i18nLocale={i18nLocale} />
              ) : isNoteProps(props) ? (
                <NoteWidget isInEditMode {...props} i18nLocale={i18nLocale} />
              ) : isPowerBiIframeProps(props) ? (
                <PowerBiIframeWidget
                  isInEditMode
                  {...props}
                  i18nLocale={i18nLocale}
                />
              ) : isChartProps(props) ? (
                <ChartWidget
                  dashboardContainerRef={dashboardContainerRef.current}
                  {...props}
                  i18nLocale={i18nLocale}
                />
              ) : null}
            </WidgetContainer>
          ))}

          {!isDragging && !isInLinkDashboardFiltersMode && (
            <div key={WIDGET_ADD_BUTTON_ID}>
              <AddWidgetButtons
                {...addWidgetButtonProps}
                i18nLocale={i18nLocale}
              />
            </div>
          )}
        </ReactGridLayout>
      </WidgetsContainer>
    </Container>
  );
};

export { EditModeDashboard };
